import { get, post } from '@/lib/api';
import { rec } from './normalize';
import type {
  ColumnMapping,
  ImportBatch,
  ImportBatchDetail,
  ImportMatch,
  ImportSummary,
  NormalizedImport,
  PendingImport,
  ScanResult
} from '@/types';

export interface StageImportInput {
  /** 'harvest' | 'csv' | 'scan' | ...: whatever produced `data`. */
  source: string;
  options?: Record<string, unknown>;
  externalRef?: string;
  data: NormalizedImport;
}

export interface StagedImport {
  id: string;
  summary: ImportSummary;
}

/** Stage a normalized import as a pending batch. Nothing is written to the books yet. */
export const stageImport = (input: StageImportInput) =>
  post<StagedImport>('/api/imports', input);

/**
 * Every batch of the team, newest first. The server returns a bare array
 * today; `{ batches }` and `{ imports }` are accepted in case it grows an
 * envelope.
 */
export async function listImports(signal?: AbortSignal): Promise<ImportBatch[]> {
  const raw = await get<unknown>('/api/imports', undefined, signal);
  if (Array.isArray(raw)) return raw as ImportBatch[];
  const r = rec(raw);
  const list = r.batches ?? r.imports ?? r.data;
  return Array.isArray(list) ? (list as ImportBatch[]) : [];
}

/**
 * The newest pending batch, with its entry and client items. The route wraps
 * null as `{ batch: null }`; a bare batch object is handled too.
 */
export async function pendingImport(signal?: AbortSignal): Promise<PendingImport> {
  const raw = await get<unknown>('/api/imports/pending', undefined, signal);
  if (raw === null || raw === undefined) return { batch: null };
  const r = rec(raw);
  if ('batch' in r) return { batch: (r.batch as PendingImport['batch']) ?? null };
  return { batch: r.id ? (r as unknown as NonNullable<PendingImport['batch']>) : null };
}

export async function getImport(id: string, signal?: AbortSignal): Promise<ImportBatchDetail> {
  const raw = await get<unknown>(`/api/imports/${id}`, undefined, signal);
  const r = rec(raw);
  return (r.batch ? r.batch : r) as ImportBatchDetail;
}

export type MatchTarget = { itemId: string; groupKey?: never } | { groupKey: string; itemId?: never };

/** Re-point one item or a whole client group at an existing row, a new one, or nothing. */
export const setMatch = (id: string, target: MatchTarget, match: ImportMatch) =>
  post<{ updated: number }>(`/api/imports/${id}/match`, { ...target, match });

export type AcceptMode = 'all' | 'matched';

/**
 * Create every row of the batch. 'all' refuses (409, body carries `refused`
 * and the unmatched `groups`) while any group is unmatched; 'matched' accepts
 * what matched and skips the rest. The 409 surfaces as an ApiError whose
 * `body` is the refusal.
 */
export const acceptImport = (id: string, mode: AcceptMode = 'all') =>
  post<{ ok: true; counts: Record<string, number> }>(`/api/imports/${id}/accept`, { mode });

export const discardImport = (id: string) => post<{ ok: true }>(`/api/imports/${id}/discard`);

/** Delete everything an accepted batch created. 409 with `refused` when it cannot. */
export const undoImport = (id: string) => post<ImportBatch>(`/api/imports/${id}/undo`);

export const aiMapColumns = (input: { headers: string[]; sample: string[][] }) =>
  post<ColumnMapping>('/api/imports/ai/map-columns', input);

/** `image` is base64 without the data: prefix. */
export const aiScan = (input: { image: string; mime: string; kind: 'timesheet' | 'invoice' }) =>
  post<ScanResult>('/api/imports/ai/scan', input);

/** "Using something else?": a platform to support, saved for /admin/import-requests. */
export const requestImportSource = (platform: string) =>
  post<{ ok: true }>('/api/imports/requests', { platform });

export interface ScanHandoff {
  id: string;
  /** The secret for the phone's link; only returned when the handoff is made. */
  token: string;
  kind: 'timesheet' | 'invoice';
  expiresAt: string;
}

export interface ScanHandoffPage {
  n: number;
  result?: ScanResult;
  error?: string;
}

export interface ScanHandoffStatus {
  id: string;
  kind: 'timesheet' | 'invoice';
  pages: ScanHandoffPage[];
  phoneConnected: boolean;
  expiresAt: string;
  expired: boolean;
}

/** A one-time link for scanning paper with a phone. */
export const createScanHandoff = (kind: 'timesheet' | 'invoice') =>
  post<ScanHandoff>('/api/imports/scan-handoffs', { kind });

export const getScanHandoff = (id: string, signal?: AbortSignal) =>
  get<ScanHandoffStatus>(`/api/imports/scan-handoffs/${id}`, undefined, signal);

/** The phone's side: no session, the token is the credential. */
export const openPhoneScan = (token: string, signal?: AbortSignal) =>
  get<{ kind: 'timesheet' | 'invoice'; pages: number; maxPages: number; expiresAt: string }>(
    `/api/scan/${token}`,
    undefined,
    signal
  );

export const sendPhoneScanPage = (token: string, input: { image: string; mime: string }) =>
  post<{ n: number; error: string | null }>(`/api/scan/${token}/pages`, input);
